/**
 * Profile ▸ Notifications — the settings matrix (`specs/M5-notifications.md` §6,
 * `discovery/ui-reference/notifications/settings-matrix.json`).
 *
 * 44 px rows, two channel columns, section headers in monday's order, and a `Muted Boards / n`
 * block at the bottom. A channel monday forces renders **checked and disabled**; a channel a
 * kind does not have renders an empty cell — both measured, both reproduced here.
 */
import { PageEmptyState, PageSkeleton } from '@elizmonday/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatHour } from '../tasks/TaskSettingsPanel';
import { useTaskSettings, type TaskSettingsPort } from '../tasks/settings-store';
import type { BoardMuteView, NotificationsSource, SettingsRowView } from './types';

export const SETTINGS_SECTION_ORDER = [
  'Communication',
  'Collaboration',
  'Requests',
  'Automations',
  'Sign-ups',
  'Security',
  'Admin',
  'Agents',
] as const;

/** The two columns we render; `Slack` waits for M10. */
export const SETTINGS_CHANNELS = ['Monday', 'Email'] as const;

/** monday's copy for the in-app column, with our product name `[ui §2]`. */
export const CHANNEL_LABELS: Record<(typeof SETTINGS_CHANNELS)[number], string> = {
  Monday: 'In Eli Z Monday',
  Email: 'Email',
};

export function groupSettings(rows: readonly SettingsRowView[]): Array<[string, SettingsRowView[]]> {
  const shown = rows.filter((row) => row.supported);
  return SETTINGS_SECTION_ORDER.map((section) => [section, shown.filter((row) => row.section === section)] as [
    string,
    SettingsRowView[],
  ]).filter(([, list]) => list.length > 0);
}

/** The title / sub-line split monday shows: the first clause is bold, the rest is grey. */
export function splitDescription(description: string): { title: string; hint: string } {
  const match = /^([^,]+?)\s(in|on|to|from|with|when|by|notify|via|that|for|after|of)\s(.*)$/.exec(description);
  if (!match) return { title: description, hint: '' };
  return { title: match[1] as string, hint: `${match[2] as string} ${match[3] as string}` };
}

/** Where the Tasks section sends somebody for the settings it does **not** own (P-321). */
export const TASKS_PREFERENCES_HREF = '/tasks';

export interface TaskNotificationsSectionProps {
  /** `tasks.settings` get/set — the same port the Preferences pane writes through. */
  tasks: TaskSettingsPort;
}

/**
 * **P-321 · the Tasks section**, at the end of Profile ▸ Notifications.
 *
 * Roberto's report was that the Tasks e-mails were the only ones not manageable where every
 * other e-mail is managed (they were in Tasks ▸ Preferences only). The two that *are*
 * notifications are repeated here — the daily digest with its hour, and the due/overdue
 * reminder — bound through `useTaskSettings` to the very store the Preferences pane uses, so a
 * switch flipped on either surface is the value the other renders with no reload.
 *
 * What stays behind: the **archive delay** (it moves rows between tabs, it sends nothing), the
 * weekly digest option and "tell me when a task I assigned is completed" (a bell notification,
 * not an e-mail). The hint line links the two surfaces to each other rather than copying them.
 *
 * The matrix above is monday's 31 kinds verbatim, so this is its **own card** rather than a
 * section inside it: adding a row to the captured matrix would break the parity it is measured
 * against (`discovery/ui-reference/notifications/settings-matrix.json`).
 */
export function TaskNotificationsSection({ tasks }: TaskNotificationsSectionProps) {
  const { settings, save } = useTaskSettings(tasks);
  /* Nothing is drawn until the read answers: a card showing `Off` before its value arrived is a
     switch that lies for a frame, and this one sends e-mail. */
  if (!settings) return null;

  /*
   * One checkbox over a three-valued setting: the card asks for on/off here, and `weekly` is
   * reachable only from Tasks ▸ Preferences (the hint says so). `on` therefore means `daily`,
   * which is the value this row is named after; `weekly` reads as on and is left alone until
   * somebody turns it off.
   */
  const digestOn = settings.digestFrequency !== 'off';

  return (
    <section
      className="ez-notif-settings__card ez-notif-tasks"
      data-testid="notification-settings-tasks"
    >
      <div className="ez-notif-settings__head">
        <h2>Tasks</h2>
        <div className="ez-notif-settings__channels">
          <div className="ez-notif-settings__channel">{CHANNEL_LABELS.Email}</div>
        </div>
      </div>
      <p className="ez-notif-tasks__hint">
        {'Sent from My Tasks. The archive delay and the weekly digest stay in '}
        <a href={TASKS_PREFERENCES_HREF} data-testid="settings-tasks-preferences-link">
          Tasks ▸ Preferences
        </a>
        .
      </p>

      <div className="ez-notif-settings__row" data-testid="settings-tasks-row-digest">
        <div className="ez-notif-settings__label">
          <strong>Daily task digest</strong>
          <span>“Your tasks today”, sent at your own hour.</span>
        </div>
        {digestOn ? (
          <select
            className="ez-notif-tasks__hour"
            data-testid="settings-tasks-hour"
            aria-label="Send the daily task digest at"
            value={String(settings.digestHour)}
            onChange={(event) => save({ digestHour: Number(event.target.value) })}
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>
                {formatHour(hour)}
              </option>
            ))}
          </select>
        ) : null}
        <label className="ez-notif-settings__box">
          <input
            type="checkbox"
            checked={digestOn}
            aria-label={`Daily task digest — ${CHANNEL_LABELS.Email}`}
            data-testid="settings-tasks-digest"
            onChange={(event) => save({ digestFrequency: event.target.checked ? 'daily' : 'off' })}
          />
        </label>
      </div>

      <div className="ez-notif-settings__row" data-testid="settings-tasks-row-remind">
        <div className="ez-notif-settings__label">
          <strong>Due and overdue reminders</strong>
          <span>One summary a day at the hour above, never one notification per task.</span>
        </div>
        <label className="ez-notif-settings__box">
          <input
            type="checkbox"
            checked={settings.remindOverdue}
            aria-label={`Due and overdue reminders — ${CHANNEL_LABELS.Email}`}
            data-testid="settings-tasks-remind"
            onChange={(event) => save({ remindOverdue: event.target.checked })}
          />
        </label>
      </div>
    </section>
  );
}

export interface NotificationSettingsProps {
  source: NotificationsSource;
  /**
   * **P-321** — the Tasks source behind the Tasks section. Optional: the matrix is a complete
   * page without it (and every unit test of the matrix renders it that way), while the product
   * route and the harness both pass one.
   */
  tasks?: TaskSettingsPort;
}

export function NotificationSettings({ source, tasks }: NotificationSettingsProps) {
  const [rows, setRows] = useState<SettingsRowView[] | null>(null);
  const [mutes, setMutes] = useState<BoardMuteView[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [settings, boardMutes] = await Promise.all([source.settings(), source.boardMutes()]);
        if (cancelled) return;
        setRows(settings);
        setMutes(boardMutes);
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load your settings');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source]);

  const toggle = useCallback(
    async (kind: string, channel: 'Monday' | 'Email', enabled: boolean) => {
      setRows((current) =>
        current
          ? current.map((row) =>
              row.kind === kind
                ? {
                    ...row,
                    channels: row.channels.map((entry) =>
                      entry.name === channel ? { ...entry, enabled } : entry,
                    ),
                  }
                : row,
            )
          : current,
      );
      await source.updateSetting({ kind, channel, enabled }).catch(() => undefined);
    },
    [source],
  );

  const unmute = useCallback(
    async (mute: BoardMuteView) => {
      setMutes((current) => current.filter((entry) => entry.boardId !== mute.boardId));
      await source.setBoardMute({ ...mute, muteState: 'NOT_MUTED', enabled: [] }).catch(() => undefined);
    },
    [source],
  );

  const sections = useMemo(() => groupSettings(rows ?? []), [rows]);

  if (error) return <PageEmptyState title="Notification settings are unavailable" hint={error} data-testid="settings-error" />;
  if (!rows) return <PageSkeleton variant="page" rows={8} data-testid="settings-loading" />;

  return (
    <div className="ez-notif-settings" data-testid="notification-settings">
      <h1 className="ez-notif-settings__title">Notifications</h1>
      <p className="ez-notif-settings__subtitle">Manage your notification preferences</p>

      <section className="ez-notif-settings__card">
        <div className="ez-notif-settings__head">
          <h2>System Notifications</h2>
          <div className="ez-notif-settings__channels">
            {SETTINGS_CHANNELS.map((channel) => (
              <div key={channel} className="ez-notif-settings__channel" data-testid={`settings-channel-${channel}`}>
                {CHANNEL_LABELS[channel]}
              </div>
            ))}
          </div>
        </div>

        {sections.map(([section, list]) => (
          <div key={section}>
            <div className="ez-notif-settings__section" data-testid="settings-section">
              {section}
            </div>
            {list.map((row) => {
              const { title, hint } = splitDescription(row.description);
              return (
                <div className="ez-notif-settings__row" key={row.kind} data-testid={`settings-row-${row.kind}`}>
                  <div className="ez-notif-settings__label">
                    <strong>{title}</strong>
                    {hint ? <span>{hint}</span> : null}
                  </div>
                  {SETTINGS_CHANNELS.map((channel) => {
                    const state = row.channels.find((entry) => entry.name === channel);
                    const missing = state?.editableStatus === 'AllRelatedNotificationsDontHaveChannel';
                    return (
                      /*
                       * A `<label>`, not a `<div>` (M0-F9 §7 F9-D10): on a phone the 13 px
                       * checkbox cannot be the tap target, so the 44 px cell around it has to
                       * forward the tap — which is exactly what a wrapping label does. The box
                       * renders identically at 1440.
                       */
                      <label className="ez-notif-settings__box" key={channel}>
                        {missing ? null : (
                          <input
                            type="checkbox"
                            checked={state?.enabled ?? false}
                            disabled={state?.editableStatus !== 'Editable'}
                            aria-label={`${title} — ${CHANNEL_LABELS[channel]}`}
                            data-testid={`settings-${row.kind}-${channel}`}
                            onChange={(event) => void toggle(row.kind, channel, event.target.checked)}
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </section>

      <section className="ez-notif-mutes" data-testid="muted-boards">
        <h2>{`Muted Boards / ${mutes.length}`}</h2>
        <p>These are boards you muted for yourself</p>
        {mutes.map((mute) => (
          <div className="ez-notif-mutes__row" key={mute.boardId}>
            <span>{mute.boardName ?? mute.boardId}</span>
            <button type="button" onClick={() => void unmute(mute)} data-testid={`unmute-${mute.boardId}`}>
              Unmute
            </button>
          </div>
        ))}
      </section>

      {/* P-321 · last on the page, after the mutes, in the page's own row style. */}
      {tasks ? <TaskNotificationsSection tasks={tasks} /> : null}
    </div>
  );
}
